import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { submitProductRequest } from "@/lib/api";
import { track } from "@/lib/analytics";
import { useFormTracking } from "@/hooks/useFormTracking";

export default function ProductRequestModal({ productName, trigger }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const formTrack = useFormTracking(`product_request:${productName}`);

  const change = (k) => (e) => {
    const v = e.target.value;
    setForm({ ...form, [k]: v });
    formTrack.onChange(k, v);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please add your name and email.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        product_name: productName,
        name: form.name.trim(),
        email: form.email.trim(),
      };
      if (form.phone.trim()) payload.phone = form.phone.trim();
      if (form.message.trim()) payload.message = form.message.trim();
      await submitProductRequest(payload);
      track("product_request_submit", { product_name: productName });
      formTrack.markSubmitted();
      toast.success("Request received. We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", message: "" });
      setOpen(false);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (v) track("product_request_open", { product_name: productName });
      }}
    >
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        data-testid={`product-request-modal-${productName
          .toLowerCase()
          .replace(/\s+/g, "-")}`}
        className="sm:max-w-xl bg-white border border-[#e5e7eb] rounded-none p-0"
      >
        <div className="p-8 md:p-10">
          <DialogHeader className="text-left mb-6">
            <span className="ash-eyebrow">Request This Product</span>
            <DialogTitle className="mt-3 text-3xl font-bold tracking-tight text-[#080808]">
              {productName}
            </DialogTitle>
            <DialogDescription className="text-[#3C4048] mt-3">
              Tell us a little about what you’re hoping to address — we’ll
              respond with availability and a recommendation.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={onSubmit} className="grid gap-6">
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="ash-label" htmlFor="pr-name">
                  Your Name
                </label>
                <input
                  id="pr-name"
                  data-testid="product-request-name-input"
                  className="ash-input"
                  placeholder="Full name"
                  value={form.name}
                  onChange={change("name")}
                  onFocus={() => formTrack.onFocus("name")}
                />
              </div>
              <div>
                <label className="ash-label" htmlFor="pr-email">
                  Email
                </label>
                <input
                  id="pr-email"
                  data-testid="product-request-email-input"
                  type="email"
                  className="ash-input"
                  placeholder="you@example.com"
                  value={form.email}
                  onChange={change("email")}
                  onFocus={() => formTrack.onFocus("email")}
                />
              </div>
            </div>
            <div>
              <label className="ash-label" htmlFor="pr-phone">
                Phone (optional)
              </label>
              <input
                id="pr-phone"
                data-testid="product-request-phone-input"
                className="ash-input"
                placeholder="+1 441…"
                value={form.phone}
                onChange={change("phone")}
                onFocus={() => formTrack.onFocus("phone")}
              />
            </div>
            <div>
              <label className="ash-label" htmlFor="pr-message">
                Message (optional)
              </label>
              <textarea
                id="pr-message"
                data-testid="product-request-message-input"
                className="ash-input ash-textarea"
                placeholder="What are you hoping this product will help with?"
                value={form.message}
                onChange={change("message")}
                onFocus={() => formTrack.onFocus("message")}
              />
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="ash-link-underline"
                data-testid="product-request-cancel-button"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="ash-btn-primary disabled:opacity-60"
                data-testid="product-request-submit-button"
              >
                {loading ? "Submitting…" : "Send Request"}
              </button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
